import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import * as Clipboard from "expo-clipboard";
import * as Sharing from "expo-sharing";
import { useCallback } from "react";
import { useFragment } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import { LoadingSpinner } from "@whoosh/design-system/src/components/display/Loader/LoadingSpinner";
import {
  CopyIcon,
  ShareIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import {
  Box,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { HStack } from "@whoosh/design-system/src/components/primitive/Stack/HStack";

import { IconCardStack } from "../BookingSummary/components/InviteOthersBox/components/IconCardStack/IconCardStack";
import { useShareBooking } from "../BookingSummary/components/InviteOthersBox/hooks/useShareBooking/useShareBooking";
import { BookingConfirmedModalShareSection_Booking$key } from "./__generated__/BookingConfirmedModalShareSection_Booking.graphql";

export const BookingConfirmedModalShareSection = ({
  bookingRef,
}: {
  bookingRef: BookingConfirmedModalShareSection_Booking$key;
}) => {
  const booking = useFragment(InviteActionsFragmentString, bookingRef);

  const shareUrl = booking?.shareUrl;

  const { handleShareBooking, isSharing } = useShareBooking();

  const { showToast } = useToast();
  const handleSharing = useCallback(async () => {
    if (shareUrl) {
      Sharing.shareAsync(shareUrl);
    } else if (booking?.id) {
      handleShareBooking({
        input: {
          bookingId: booking.id,
        },
        onSuccessCallback: (shareUrl) => {
          Sharing.shareAsync(shareUrl);
        },
      });
    }
  }, [booking?.id, handleShareBooking, shareUrl]);

  const copyToClipboard = useCallback(async () => {
    if (shareUrl) {
      await Clipboard.setStringAsync(shareUrl);
    } else if (booking?.id) {
      handleShareBooking({
        input: {
          bookingId: booking.id,
        },
        onSuccessCallback: (shareUrl) => {
          Clipboard.setStringAsync(shareUrl);
        },
      });
    }
    showToast({
      semanticStyle: "success",
      title: t`Link copied to clipboard`,
      description: t`Nice! I always wanted to meet your friends.`,
    });
  }, [booking?.id, handleShareBooking, shareUrl, showToast]);

  return (
    <VStack
      width="100%"
      backgroundColor={"surface-primary"}
      padding={5}
      borderRadius="4"
      alignItems={"center"}
      gap={6}
    >
      <Box
        width="100%"
        borderRadius="4"
        style={{
          height: 84,
          marginTop: -(84 / 2) + 24, // half of the height of the icon card stack + 24px to center it and add 24px of padding above
        }}
      >
        <IconCardStack isMobile={false} size="sm" />
      </Box>
      <Text variant="mainBaseSemiBold">{t`Invite others to this booking`}</Text>
      <Text
        variant="mainSmallMedium"
        color="text-secondary"
        textAlign={"center"}
      >
        {t`Share the booking with other players to ensure everyone stays informed and nothing important is missed.`}
      </Text>
      <HStack gap={4} width="100%">
        <Button
          size="small"
          accessibilityLabel={t`Share Button`}
          testID="invite-actions-share-button"
          style={{ flex: 1 }}
          variant="outline"
          leftIcon={isSharing ? <LoadingSpinner /> : <ShareIcon />}
          onPress={handleSharing}
          isDisabled={isSharing}
        >{t`Share`}</Button>
        <Button
          size="small"
          accessibilityLabel={t`Copy Link Button`}
          testID="invite-actions-copy-link-button"
          variant="outline"
          onPress={copyToClipboard}
          leftIcon={isSharing ? <LoadingSpinner /> : <CopyIcon />}
          isDisabled={isSharing}
          style={{ flex: 1 }}
        >
          {t`Copy Link`}
        </Button>
      </HStack>
    </VStack>
  );
};

const InviteActionsFragmentString = graphql`
  fragment BookingConfirmedModalShareSection_Booking on Booking {
    id
    shareUrl
  }
`;
